import { supabase } from '@/integrations/supabase/client';

export interface ChipChipSalesData {
  product_name: string;
  date: string;
  order_type: string;
  price: number;
  num_order: number;
  quantity: number;
}

export interface ChipChipPriceData {
  date: string;
  price: number;
  location: string;
  product_name: string;
  source: string;
}

/**
 * Fetch ChipChip sales data for multiple products
 * Since the API doesn't support multiple products, we make parallel calls
 */
export const fetchChipChipMultiProductData = async (
  products: string[],
  startDate: string,
  endDate: string
): Promise<ChipChipPriceData[]> => {
  console.log('Fetching ChipChip data for multiple products:', products);
  
  try {
    // Call the dedicated chipchip-sales edge function
    const { data, error } = await supabase.functions.invoke('chipchip-sales', {
      body: {
        products,
        startDate,
        endDate
      }
    });

    if (error) {
      console.error('ChipChip API error:', error);
      return [];
    }

    if (data?.success && data?.data) {
      console.log(`Successfully fetched ${data.data.length} ChipChip records`);
      return data.data;
    }

    console.log('No ChipChip data returned');
    return [];
    
  } catch (error) {
    console.error('Error fetching ChipChip multi-product data:', error);
    return [];
  }
};

/**
 * Fetch ChipChip sales data for a single product
 */
const fetchChipChipSingleProduct = async (
  productName: string,
  startDate: string,
  endDate: string
): Promise<ChipChipSalesData[]> => {
  try {
    // Call the chippie-ai edge function to get ChipChip data
    const { data, error } = await supabase.functions.invoke('chippie-ai', {
      body: {
        message: `Get ChipChip sales data for ${productName} from ${startDate} to ${endDate}`,
        conversation_history: [],
        chipchip_only: true, // Flag to indicate we only want ChipChip data
        product_name: productName,
        start_date: startDate,
        end_date: endDate
      }
    });

    if (error) {
      console.error(`ChipChip API error for ${productName}:`, error);
      return [];
    }

    // Extract ChipChip data from the response
    if (data?.data && Array.isArray(data.data)) {
      return data.data.filter((item: any) => item.source === 'chipchip_sales');
    }

    return [];
    
  } catch (error) {
    console.error(`Error fetching ChipChip data for ${productName}:`, error);
    return [];
  }
};

/**
 * Alternative direct API call method (if edge function approach doesn't work)
 */
export const fetchChipChipDirectAPI = async (
  productName: string,
  startDate: string,
  endDate: string
): Promise<ChipChipSalesData[]> => {
  // This would require the CHIPCHIP_API_TOKEN to be available on the frontend
  // which is not recommended for security reasons
  console.warn('Direct ChipChip API calls not recommended from frontend');
  return [];
};

/**
 * Transform ChipChip data to match our price data format
 */
export const transformChipChipData = (data: ChipChipSalesData[]): ChipChipPriceData[] => {
  return data.map(item => ({
    date: item.date,
    price: item.price,
    location: 'ChipChip Sales',
    product_name: item.product_name,
    source: 'chipchip_sales'
  }));
};